/**
 * facilities.ts - Auxiliary facility definitions
 */

export type FacilityId =
  | 'haunted_house'
  | 'ticket_booth'
  | 'gift_shop'
  | 'snack_bar'
  | 'photo_booth'
  | 'restroom'
  | 'parking_lot'
  | 'first_aid'
  | 'arcade'
  | 'fortune_teller'
  | 'escape_room'
  | 'stage'
  | 'voodoo_shop'
  | 'ghost_cafe'
  | 'phantom_cinema'
  | 'skeleton_spa';

export interface FacilityDef {
  id: FacilityId;
  nameKey: string;
  descKey: string;
  buildCost: number;        // SP cost to build
  maxLevel: number;         // 1-3
  upgradeCostMultiplier: number; // cost * this per level
  spPerTick: number;        // passive SP earned per visitor group completion (base)
  bonusEffect: string;      // description key for bonus effect
}

export const FACILITY_DEFS: Record<FacilityId, FacilityDef> = {
  haunted_house: {
    id: 'haunted_house',
    nameKey: 'facility_haunted_house',
    descKey: 'facility_haunted_house_desc',
    buildCost: 0,
    maxLevel: 1,
    upgradeCostMultiplier: 1,
    spPerTick: 0,
    bonusEffect: 'facility_haunted_house_bonus',
  },
  ticket_booth: {
    id: 'ticket_booth',
    nameKey: 'facility_ticket_booth',
    descKey: 'facility_ticket_booth_desc',
    buildCost: 0,
    maxLevel: 5,
    upgradeCostMultiplier: 2.0,
    spPerTick: 0,
    bonusEffect: 'facility_ticket_booth_bonus',
  },
  gift_shop: {
    id: 'gift_shop',
    nameKey: 'facility_gift_shop',
    descKey: 'facility_gift_shop_desc',
    buildCost: 500,
    maxLevel: 3,
    upgradeCostMultiplier: 3.0,
    spPerTick: 5,
    bonusEffect: 'facility_gift_shop_bonus',
  },
  snack_bar: {
    id: 'snack_bar',
    nameKey: 'facility_snack_bar',
    descKey: 'facility_snack_bar_desc',
    buildCost: 600,
    maxLevel: 3,
    upgradeCostMultiplier: 2.5,
    spPerTick: 5,
    bonusEffect: 'facility_snack_bar_bonus',
  },
  photo_booth: {
    id: 'photo_booth',
    nameKey: 'facility_photo_booth',
    descKey: 'facility_photo_booth_desc',
    buildCost: 1200,
    maxLevel: 3,
    upgradeCostMultiplier: 3.0,
    spPerTick: 8,
    bonusEffect: 'facility_photo_booth_bonus',
  },
  restroom: {
    id: 'restroom',
    nameKey: 'facility_restroom',
    descKey: 'facility_restroom_desc',
    buildCost: 400,
    maxLevel: 5,
    upgradeCostMultiplier: 2.0,
    spPerTick: 1,
    bonusEffect: 'facility_restroom_bonus',
  },
  parking_lot: {
    id: 'parking_lot',
    nameKey: 'facility_parking_lot',
    descKey: 'facility_parking_lot_desc',
    buildCost: 1000,
    maxLevel: 5,
    upgradeCostMultiplier: 2.5,
    spPerTick: 2,
    bonusEffect: 'facility_parking_lot_bonus',
  },
  first_aid: {
    id: 'first_aid',
    nameKey: 'facility_first_aid',
    descKey: 'facility_first_aid_desc',
    buildCost: 1200,
    maxLevel: 5,
    upgradeCostMultiplier: 2.0,
    spPerTick: 1,
    bonusEffect: 'facility_first_aid_bonus',
  },
  arcade: {
    id: 'arcade',
    nameKey: 'facility_arcade',
    descKey: 'facility_arcade_desc',
    buildCost: 1000,
    maxLevel: 3,
    upgradeCostMultiplier: 3.0,
    spPerTick: 6,
    bonusEffect: 'facility_arcade_bonus',
  },
  fortune_teller: {
    id: 'fortune_teller',
    nameKey: 'facility_fortune_teller',
    descKey: 'facility_fortune_teller_desc',
    buildCost: 1500,
    maxLevel: 3,
    upgradeCostMultiplier: 2.5,
    spPerTick: 10,
    bonusEffect: 'facility_fortune_teller_bonus',
  },
  escape_room: {
    id: 'escape_room',
    nameKey: 'facility_escape_room',
    descKey: 'facility_escape_room_desc',
    buildCost: 2000,
    maxLevel: 3,
    upgradeCostMultiplier: 2.5,
    spPerTick: 14,
    bonusEffect: 'facility_escape_room_bonus',
  },
  stage: {
    id: 'stage',
    nameKey: 'facility_stage',
    descKey: 'facility_stage_desc',
    buildCost: 2000,
    maxLevel: 3,
    upgradeCostMultiplier: 2.5,
    spPerTick: 4,
    bonusEffect: 'facility_stage_bonus',
  },
  voodoo_shop: {
    id: 'voodoo_shop',
    nameKey: 'facility_voodoo_shop',
    descKey: 'facility_voodoo_shop_desc',
    buildCost: 1800,
    maxLevel: 3,
    upgradeCostMultiplier: 2.8,
    spPerTick: 12,
    bonusEffect: 'facility_voodoo_shop_bonus',
  },
  ghost_cafe: {
    id: 'ghost_cafe',
    nameKey: 'facility_ghost_cafe',
    descKey: 'facility_ghost_cafe_desc',
    buildCost: 800,
    maxLevel: 5,
    upgradeCostMultiplier: 2.2,
    spPerTick: 4,
    bonusEffect: 'facility_ghost_cafe_bonus',
  },
  phantom_cinema: {
    id: 'phantom_cinema',
    nameKey: 'facility_phantom_cinema',
    descKey: 'facility_phantom_cinema_desc',
    buildCost: 2500,
    maxLevel: 3,
    upgradeCostMultiplier: 3.0,
    spPerTick: 16,
    bonusEffect: 'facility_phantom_cinema_bonus',
  },
  skeleton_spa: {
    id: 'skeleton_spa',
    nameKey: 'facility_skeleton_spa',
    descKey: 'facility_skeleton_spa_desc',
    buildCost: 1500,
    maxLevel: 5,
    upgradeCostMultiplier: 2.0,
    spPerTick: 3,
    bonusEffect: 'facility_skeleton_spa_bonus',
  },
};

export const ALL_FACILITY_IDS = Object.keys(FACILITY_DEFS) as FacilityId[];

export function facilityCostForLevel(facilityId: FacilityId, level: number): number {
  const def = FACILITY_DEFS[facilityId];
  if (!def) return 0;
  if (level <= 1) return def.buildCost;
  return Math.floor(def.buildCost * Math.pow(def.upgradeCostMultiplier, level - 1));
}
